import apiClient from './apiClient';
import {
  PsychosocialTemplate,
  PsychosocialTemplateDetail,
  PsychosocialForm,
  PsychosocialCampaign,
  PsychosocialCampaignEmployee,
  PsychosocialPublicFormData,
  PsychosocialDashboardData,
  PsychosocialResult,
  CreateFormRequest,
  CreateCampaignRequest,
  AddEmployeesRequest,
  EmployeeSearchResult,
  Pagination,
} from '../types/psychosocial';

const extractData = (response: any) => response.data ?? response;
const extractPaginated = (response: any) => ({
  data: response.data || [],
  pagination: response.pagination as Pagination,
});

class PsychosocialService {
  private baseUrl = '/psychosocial';

  // ── Templates ──

  async getTemplates(): Promise<PsychosocialTemplate[]> {
    const response = await apiClient.get(`${this.baseUrl}/templates`);
    return extractData(response);
  }

  async getTemplate(id: number): Promise<PsychosocialTemplateDetail> {
    const response = await apiClient.get(`${this.baseUrl}/templates/${id}`);
    return extractData(response);
  }

  // ── Forms ──

  async getForms(params?: { companyId?: number; search?: string; page?: number; limit?: number }): Promise<{ data: PsychosocialForm[]; pagination: Pagination }> {
    const response = await apiClient.get(`${this.baseUrl}/forms`, { params });
    return extractPaginated(response);
  }

  async getForm(id: number): Promise<PsychosocialForm> {
    const response = await apiClient.get(`${this.baseUrl}/forms/${id}`);
    return extractData(response);
  }

  async createForm(data: CreateFormRequest): Promise<PsychosocialForm> {
    const response = await apiClient.post(`${this.baseUrl}/forms`, data);
    return extractData(response);
  }

  async updateForm(id: number, data: any): Promise<PsychosocialForm> {
    const response = await apiClient.put(`${this.baseUrl}/forms/${id}`, data);
    return extractData(response);
  }

  async deleteForm(id: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/forms/${id}`);
  }

  // ── Campaigns ──

  async getCampaigns(params?: { companyId?: number; status?: string; search?: string; page?: number; limit?: number }): Promise<{ data: PsychosocialCampaign[]; pagination: Pagination }> {
    const response = await apiClient.get(`${this.baseUrl}/campaigns`, { params });
    return extractPaginated(response);
  }

  async getCampaign(id: number): Promise<PsychosocialCampaign> {
    const response = await apiClient.get(`${this.baseUrl}/campaigns/${id}`);
    return extractData(response);
  }

  async createCampaign(data: CreateCampaignRequest): Promise<PsychosocialCampaign> {
    const response = await apiClient.post(`${this.baseUrl}/campaigns`, data);
    return extractData(response);
  }

  async updateCampaign(id: number, data: Partial<CreateCampaignRequest>): Promise<PsychosocialCampaign> {
    const response = await apiClient.put(`${this.baseUrl}/campaigns/${id}`, data);
    return extractData(response);
  }

  async deleteCampaign(id: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/campaigns/${id}`);
  }

  async activateCampaign(id: number): Promise<{ totalNotified: number }> {
    const response = await apiClient.post(`${this.baseUrl}/campaigns/${id}/activate`);
    return extractData(response);
  }

  async closeCampaign(id: number): Promise<PsychosocialCampaign> {
    const response = await apiClient.post(`${this.baseUrl}/campaigns/${id}/close`);
    return extractData(response);
  }

  async archiveCampaign(id: number): Promise<PsychosocialCampaign> {
    const response = await apiClient.post(`${this.baseUrl}/campaigns/${id}/archive`);
    return extractData(response);
  }

  // ── Campaign Employees ──

  async getCampaignEmployees(campaignId: number, params?: { status?: string; search?: string; page?: number; limit?: number }): Promise<{ data: PsychosocialCampaignEmployee[]; pagination: Pagination }> {
    const response = await apiClient.get(`${this.baseUrl}/campaigns/${campaignId}/employees`, { params });
    return extractPaginated(response);
  }

  async addCampaignEmployees(campaignId: number, data: AddEmployeesRequest): Promise<{ added: number; total: number }> {
    const response = await apiClient.post(`${this.baseUrl}/campaigns/${campaignId}/employees`, data);
    return extractData(response);
  }

  async removeCampaignEmployee(campaignId: number, employeeId: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/campaigns/${campaignId}/employees/${employeeId}`);
  }

  async resendNotification(campaignId: number, employeeId: number): Promise<void> {
    await apiClient.post(`${this.baseUrl}/campaigns/${campaignId}/employees/${employeeId}/resend`);
  }

  async searchEmployeesForCampaign(campaignId: number, query: string): Promise<EmployeeSearchResult[]> {
    const response = await apiClient.get(`${this.baseUrl}/campaigns/${campaignId}/employees/search`, { params: { q: query, limit: 20 } });
    return extractData(response);
  }

  // ── Public Response ──

  async loadPublicForm(token: string): Promise<PsychosocialPublicFormData> {
    const response = await apiClient.get(`${this.baseUrl}/respond/${token}`);
    return extractData(response);
  }

  async saveDraft(token: string, answers: Record<string, number>): Promise<void> {
    await apiClient.post(`${this.baseUrl}/respond/${token}`, { answers });
  }

  async submitResponse(token: string, answers: Record<string, number>): Promise<void> {
    await apiClient.post(`${this.baseUrl}/respond/${token}/submit`, { answers });
  }

  // ── Dashboard & Results ──

  async getDashboard(campaignId: number): Promise<PsychosocialDashboardData> {
    const response = await apiClient.get(`${this.baseUrl}/campaigns/${campaignId}/dashboard`);
    return extractData(response);
  }

  async getResults(campaignId: number, params?: { departmentId?: number; occupationId?: number; classification?: string; page?: number; limit?: number }): Promise<{ data: PsychosocialResult[]; pagination: Pagination }> {
    const response = await apiClient.get(`${this.baseUrl}/campaigns/${campaignId}/results`, { params });
    return extractPaginated(response);
  }

  async exportExcel(campaignId: number, params?: { departmentId?: number; occupationId?: number; classification?: string }): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/campaigns/${campaignId}/export`, {
      params,
      responseType: 'blob',
    });
    return response as unknown as Blob;
  }
}

const psychosocialService = new PsychosocialService();
export default psychosocialService;
